import { signal } from "@preact/signals";
import { type Editor, getSchema } from "@tiptap/core";
import { DOMSerializer, Node as PMNode, type Schema } from "@tiptap/pm/model";
import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { Card, Doc } from "../../../shared/types.ts";
import { noteExtensions } from "./detail/extensions.ts";
import { FileFace, isFileKind, shownAs } from "./detail/Media.tsx";
import { autoFields, cardColors, isBlank, noteTags } from "./detail/rules.ts";
import { IconAlert, IconCheck, IconImage, IconSpin, IconUp } from "./Icons.tsx";
import { cardImage, cardText, columnsFor, contrast, DENSITY, type Density, domainInitial, estimateHeight, gridNotice, INGEST_LABEL, ingestReason, isLongNote, KIND_LABEL, masonry, safeColor, urlFromText } from "./lib.ts";
import { isWriterKey, Writer } from "./reader/Writer.tsx";
import { imagesSent, RichText } from "./RichText.tsx";
import { dragIdsFor, endCardDrag, modifierPick, selected, selecting, startCardDrag, toggleSelect } from "./spaces/selection.ts";
import { failed, refocus, resumeAt, route, scrollbar, showCard, store, toast, visible, width } from "./state.ts";

// ponytail: paginação por sentinela (60 por vez), não janela virtual. Sem :has() que alcance os cards, a página nova
// recalcula só o estilo dela: ~60 ms com 60 cards abertos, ~100 ms com 1.100 (CPU do Pixel 7 a 1/4, e2e
// mobile-browse-scroll). O resto (layout das colunas, diff do Preact) e o DOM ainda crescem com a lista: trocar por
// janela (só o que está perto da tela) se pesar com 5 mil cards ou mais rolados.
const PAGE = 60;
type Item = Card | "note";
/** Tudo (sem busca) remontado: abre até o card em que a pessoa estava (resumeAt), com meia página abaixo dele. */
function firstLimit(cards: Card[], resetKey: string) {
  const i = !resetKey && resumeAt ? cards.findIndex((c) => c.id === resumeAt!.id) : -1;
  return i < 0 ? PAGE : Math.ceil((i + 1 + PAGE / 2) / PAGE) * PAGE;
}

export const MOD = /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘" : "Ctrl";
/** Tela de toque sem mouse (celular, tablet): sem atalhos de teclado nos textos nem nas dicas. */
export const TOUCH = matchMedia("(hover: none) and (pointer: coarse)").matches;

/** Grade masonry. `lead` põe a nota rápida na primeira posição; `render` troca o card (a Lixeira usa o seu). */
export function Grid({ cards, lead, resetKey, density, render }: { cards: Card[]; lead: boolean; resetKey: string; density: Density; render?: (c: Card) => ComponentChildren }) {
  // O limite volta a uma página no mesmo render em que resetKey muda (num efeito, a busca nova sairia primeiro com o
  // limite velho: centenas de cards e a página parada no meio deles).
  const page = useRef<{ key: string; limit: number } | null>(null);
  if (!page.current) page.current = { key: resetKey, limit: firstLimit(cards, resetKey) };
  else if (page.current.key !== resetKey) page.current = { key: resetKey, limit: PAGE };
  const limit = page.current.limit;
  const [, grow] = useState(0);
  const more = useRef<HTMLDivElement>(null);
  const hasMore = cards.length > limit;
  // Recria o observer a cada página: se a sentinela continuar visível, o callback inicial pede mais uma.
  useEffect(() => {
    if (!hasMore || !more.current) return;
    const io = new IntersectionObserver((es) => es[0]?.isIntersecting && ((page.current!.limit += PAGE), grow((n) => n + 1)), { rootMargin: "1200px" });
    io.observe(more.current);
    return () => io.disconnect();
  }, [limit, hasMore, resetKey]);

  const items: Item[] = [...(lead ? ["note" as const] : []), ...cards.slice(0, limit)];
  const cols = masonry(items, columnsFor(width.value, scrollbar(), DENSITY[density].min), (it) => (it === "note" ? 0.7 : estimateHeight(it)));
  // Sem :has() na grade (nem dentro dos cards): no Chrome e no Safari, um :has() cujo argumento casa com o que entra
  // na grade faz cada página nova recalcular o estilo de todos os cards (centenas de ms com mil cards num Android
  // médio). Nota com foco e grade vazia viram atributos.
  const noting = (e: FocusEvent) => e.currentTarget instanceof Element && e.currentTarget.toggleAttribute("data-noting", e.type === "focusin" ? inNote(e.target) : inNote(e.relatedTarget));
  return (
    <>
      <div class="grid" data-density={density} data-empty={items.length ? undefined : ""} onFocusIn={noting} onFocusOut={noting}>
        {cols.map((col, i) => (
          <div class="col" key={i}>
            {col.map((it) => (it === "note" ? <QuickNote key="note" /> : render ? render(it) : <CardView key={it.id} card={it} />))}
          </div>
        ))}
      </div>
      {hasMore && <div ref={more} style="height:1px" aria-hidden="true" />}
    </>
  );
}

const order = () => visible.peek().map((c) => c.id);
const inNote = (t: EventTarget | null) => t instanceof Element && !!t.closest(".quicknote");

// ---------- Toque longo: entra no modo seleção com o card ----------
// O Safari do iPhone não dispara contextmenu no toque (mostraria a prévia do link): um temporizador no pointerdown de
// toque faz o papel. Mexer o dedo mais de 10 px, soltar, cancelar ou rolar desiste. O clique que vem do mesmo toque não
// abre o card nem desmarca. O Android dispara contextmenu: ele é engolido (ou entra, se chegar antes do temporizador).
const COARSE = matchMedia("(pointer: coarse)").matches;
const HOLD_MS = 450;
let press: { x: number; y: number; timer: ReturnType<typeof setTimeout> } | null = null;
let held = false;
let swallowUntil = 0;
const cancelPress = () => {
  if (press) clearTimeout(press.timer);
  press = null;
};
function enterSelection(id: string) {
  cancelPress();
  held = true;
  selecting.value = true;
  toggleSelect(id, order());
}
function startPress(e: PointerEvent, id: string) {
  if (e.pointerType !== "touch" || !e.isPrimary || selecting.peek()) return;
  cancelPress();
  press = { x: e.clientX, y: e.clientY, timer: setTimeout(() => enterSelection(id), HOLD_MS) };
}
addEventListener("pointermove", (e: PointerEvent) => press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 10 && cancelPress(), { passive: true });
addEventListener("pointercancel", cancelPress, true);
addEventListener("scroll", cancelPress, { capture: true, passive: true });
addEventListener(
  "pointerup",
  () => {
    cancelPress();
    if (held) swallowUntil = performance.now() + 400;
    held = false;
  },
  true,
);
addEventListener(
  "click",
  (e) => {
    if (performance.now() > swallowUntil) return;
    swallowUntil = 0;
    e.preventDefault();
    e.stopPropagation();
  },
  true,
);

/** Card de Tudo. Arrasta (para Em foco ou o nome de uma coleção); no modo seleção, um botão por cima marca e desmarca. */
function CardView({ card: c }: { card: Card }) {
  const kind = KIND_LABEL[c.kind];
  const label = cardText(c).slice(0, 120);
  // Foto sem título: o leitor de tela diz quando ela foi guardada, não só "imagem" em todas.
  const name = label === kind ? `${kind}, ${new Date(c.createdAt).toLocaleString("pt-BR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}` : `${kind}: ${label}`;
  const sel = selecting.value;
  const on = sel && selected.value.has(c.id);
  return (
    <article class="card" data-kind={c.kind} data-cap={captionOf(c) ? "" : undefined} data-selected={on || undefined} onDragStart={(e) => startCardDrag(e, dragIdsFor(c.id))} onDragEnd={endCardDrag}>
      {/* Antes do link: o anel de foco do card sai de .select-hit:focus-visible + .card-open (sem :has). */}
      {sel && (
        <button type="button" class="select-hit" aria-pressed={on} aria-label={`Selecionar ${name}`} onClick={(e) => toggleSelect(c.id, order(), e.shiftKey)}>
          <span class="select-mark" aria-hidden="true">
            {on && <IconCheck />}
          </span>
        </button>
      )}
      <a
        class="card-open"
        href={`#${encodeURIComponent(c.id)}`}
        aria-label={name}
        tabIndex={sel ? -1 : undefined}
        draggable={COARSE ? false : undefined} // no toque, segurar é selecionar (não arrastar o link)
        onClick={(e) => modifierPick(e, c.id, order())}
        onPointerDown={(e) => startPress(e, c.id)}
        onContextMenu={(e) => {
          if (!COARSE) return;
          e.preventDefault(); // nada de menu do link no lugar da seleção
          if (!selecting.peek()) enterSelection(c.id);
        }}
      >
        <Frame card={c} />
        <Caption card={c} />
      </a>
      <GridIngest card={c} />
      <ImageRetry card={c} name={name} />
    </article>
  );
}

/** Texto da legenda sob a mídia: título (imagem, nota com título) ou domínio (link). Card com legenda leva data-cap. */
export const captionOf = (c: Card) => (c.kind === "link" || (c.kind === "highlight" && !c.title) ? c.sourceDomain?.replace(/^www\./, "") : c.title);
/** Legenda sob a mídia. Sem texto, sem legenda. */
export function Caption({ card: c }: { card: Card }) {
  const text = captionOf(c);
  return text ? <p class="caption">{text}</p> : null;
}

/** O que o card mostra acima da legenda: mídia, nota ou caixa de link. */
export function Frame({ card }: { card: Card }) {
  const c = shownAs(card); // Vídeo, PDF, Áudio sem o arquivo: face de link ou de nota (gap-capture)
  const src = cardImage(c, store.previews.value, 640);
  if (c.kind === "image") {
    const ph = safeColor(c.dominantColor);
    return (
      <div class="frame media" style={`aspect-ratio:${c.width && c.height ? c.width / c.height : 1};${ph ? `--ph:${ph}` : ""}`}>
        {src && <Img src={src} />}
      </div>
    );
  }
  if (c.kind === "link") {
    return (
      <div class={`frame linkbox${src ? "" : " no-cover"}`}>
        <div class="linkbox-top">
          {src && <Favicon domain={c.sourceDomain} />}
          <p class="link-title">{c.title ?? c.sourceUrl}</p>
        </div>
        {src ? (
          <div class="linkbox-cover">
            <Img src={src} />
          </div>
        ) : (
          <p class="linkbox-foot">
            <Favicon domain={c.sourceDomain} />
            {c.sourceDomain}
          </p>
        )}
      </div>
    );
  }
  if (isFileKind(c.kind)) return <FileFace card={c} />; // PDF, vídeo, áudio (gap-capture)
  const colors = c.kind === "color" ? cardColors(c) : [];
  if (colors.length) {
    // Cor e paleta: amostras lado a lado, cada uma com o código na cor que contrasta.
    return (
      <div class="frame colors">
        {colors.slice(0, 8).map((h) => (
          <span key={h} class="hue" style={`--c:${h};--on:${contrast(h, "#ffffff") > contrast(h, "#000000") ? "#fff" : "#000"}`}>
            <span class="hue-hex">{h}</span>
          </span>
        ))}
      </div>
    );
  }
  return (
    <div class={`frame note${isLongNote(c) ? " long" : ""}`}>
      <DocView doc={c.content} text={c.contentText || cardText(c)} />
    </div>
  );
}

/**
 * Mídia que não carregou: aviso discreto no lugar e "Tentar de novo" no card (D26). A falha vale só enquanto a mídia
 * está na tela: sair dela (outra tela, voltar), a rede voltar ou a janela ganhar foco pedem o arquivo de novo.
 */
const broken = signal<ReadonlySet<string>>(new Set());
const tries = new Map<string, number>();
/** Esquece a falha de `src` (ou de todas): o próximo render pede o arquivo de novo, num <img> novo. */
function unbreak(src?: string) {
  const next = new Set(broken.value);
  for (const s of src === undefined ? broken.value : [src]) if (next.delete(s)) tries.set(s, (tries.get(s) ?? 0) + 1);
  if (next.size !== broken.value.size) broken.value = next;
}
addEventListener("online", () => unbreak());
addEventListener("focus", () => unbreak());
function Img({ src }: { src: string }) {
  useEffect(() => () => unbreak(src), [src]);
  if (broken.value.has(src)) {
    // Prévia local (blob:) que este navegador não desenha (HEIC fora do Safari): só o desenho de imagem, sem aviso nem
    // "Tentar de novo" (pediria o mesmo arquivo). A miniatura do servidor entra quando o envio termina.
    return (
      <span class="img-broken">
        <IconImage />
        {!src.startsWith("blob:") && <span>Imagem não carregou</span>}
      </span>
    );
  }
  // key muda a cada tentativa: um <img> novo pede o arquivo de novo.
  return <img key={tries.get(src) ?? 0} src={src} alt="" loading="lazy" decoding="async" onError={() => (broken.value = new Set(broken.value).add(src))} />;
}

/** Botão fora do link do card (nada interativo dentro de <a>), sobre o aviso da mídia. Toda face com <Frame> põe um. */
export function ImageRetry({ card: c, name }: { card: Card; name: string }) {
  const src = cardImage(c, store.previews.value, 640);
  if (!src || src.startsWith("blob:") || !broken.value.has(src)) return null;
  return (
    <button type="button" class="img-retry text-btn" aria-label={`Tentar de novo: ${name}`} onClick={() => unbreak(src)}>
      Tentar de novo
    </button>
  );
}

// ponytail: inicial do domínio no lugar do favicon (o card não traz favicon); trocar quando a ingestão gravar um.
export const Favicon = ({ domain }: { domain: string | null }) =>
  domain ? (
    <span class="favicon" aria-hidden="true">
      {domainInitial(domain)}
    </span>
  ) : null;

let schema: Schema | null = null;
/** Doc do TipTap → DOM só de leitura (o mesmo schema do editor). Links viram texto: o card inteiro já é um link. */
function docFragment(doc: Doc | null): DocumentFragment | null {
  if (!doc) return null;
  try {
    const s = (schema ??= getSchema(noteExtensions));
    const frag = DOMSerializer.fromSchema(s).serializeFragment(PMNode.fromJSON(s, doc).content) as DocumentFragment;
    for (const a of frag.querySelectorAll("a")) {
      const span = document.createElement("span");
      span.className = "a";
      span.append(...a.childNodes);
      a.replaceWith(span);
    }
    // Caixa de tarefa vira desenho: o card inteiro já é um link, nada interativo dentro dele.
    for (const box of frag.querySelectorAll('ul[data-type="taskList"] > li > label, input')) {
      const li = box.closest("li");
      const input = box instanceof HTMLInputElement ? box : box.querySelector("input");
      const span = document.createElement("span");
      span.className = "check";
      span.dataset.checked = li?.dataset.checked ?? String(input?.checked ?? false);
      box.replaceWith(span);
    }
    return frag;
  } catch {
    return null; // doc fora do schema: cai no texto plano
  }
}

function DocView({ doc, text }: { doc: Doc | null; text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => ref.current!.replaceChildren(docFragment(doc) ?? text), [doc, text]);
  return <div class="doc" ref={ref} />;
}

/** "Tentar de novo" recria o card (fase 1): o detalhe passa para o card novo e o foco vai com ele. */
function retry(id: string) {
  store.retry(id).then((next) => {
    if (!next) return;
    const inDetail = route.value.cardId === id;
    if (inDetail) showCard(next);
    refocus(() =>
      document.querySelector<HTMLElement>(inDetail ? "dialog.detail .detail-close" : `a.card-open[href="#${CSS.escape(encodeURIComponent(next))}"]`),
    );
  }, failed);
}

/** Arquivo subindo: "Enviando 42%" com o envio no ar; "Na fila" sem conexão (nada sobe até ela voltar). */
function sendingLabel(c: Card): string | null {
  if (c.ingestState !== "uploading") return null;
  if (!store.status.value.online) return "Na fila";
  const pct = store.progress.value.get(c.id);
  return pct === undefined ? null : `Enviando ${pct}%`;
}

/**
 * Ingestão na grade e nas coleções (gridNotice). Falhou e o Parcial que a pessoa resolve cobrem a mídia. Enviando e
 * processando, a face já aparece e o estado fica num selo no canto de baixo, sem cobrir a mídia: enviando, escrito
 * ("Enviando 42%", "Na fila" sem conexão), para quem enxerga acompanhar; processando, só o ícone girando (o nome fica
 * para o leitor de tela). Parcial por falta da IA não aparece (o detalhe explica).
 */
export function GridIngest({ card: c }: { card: Card }) {
  const n = gridNotice(c);
  if (!n) return null;
  if (n === "partial" && broken.value.has(cardImage(c, store.previews.value, 640) ?? "")) return null; // o aviso da mídia já fala
  if (n === "failed" || n === "partial") return <Ingest card={c} />;
  const up = n === "uploading";
  return (
    <p class={`ingest is-chip is-${n}`}>
      {up ? <IconUp /> : <IconSpin />}
      <span class={up ? "ingest-label" : "ingest-label sr-only"}>{(up && sendingLabel(c)) || INGEST_LABEL[n]}</span>
    </p>
  );
}

/** Falhou ou Parcial, com texto e ícone (cor nunca é o único sinal): "Rótulo: motivo". Na grade cobre a mídia. */
export function Ingest({ card: c }: { card: Card }) {
  const label = INGEST_LABEL[c.ingestState];
  if (!label) return null;
  const isFailed = c.ingestState === "failed";
  const why = ingestReason(c.ingestError);
  return (
    <p class={`ingest is-${c.ingestState}`}>
      {isFailed && <IconAlert />}
      <span class="ingest-label">{label}</span>
      {why && (
        <>
          <span class="sr-only">: </span>
          <span class="ingest-why">{why}</span>
        </>
      )}
      {isFailed && (
        <button type="button" class="text-btn" onClick={() => retry(c.id)}>
          Tentar de novo
        </button>
      )}
    </p>
  );
}

/** Botão que age sobre o editor sem tirar o foco dele antes do clique (no iPhone a folha encolheria debaixo do dedo). */
const keepFocus = (e: MouseEvent) => e.preventDefault();

// Nota rápida: o rascunho sobrevive a uma busca (o componente sai da grade e volta).
let draft: Doc | null = null;
let noteEditor: Editor | null = null;
let wantFocus = false;
let saving = false;
// view.focus() é síncrono (commands.focus espera um frame e as primeiras teclas escapariam), mas não rola a página
// (ProseMirror foca com preventScroll). A nota é sempre o primeiro item da grade: sobe para o topo.
const focusEditor = (ed: Editor) => {
  ed.view.focus();
  scrollTo({ top: 0 });
};
/** A nota rápida tem texto ainda não guardado (o rascunho só vive na memória; recarregar o perderia). */
export const hasDraft = () => !!draft || !!(noteEditor && !noteEditor.isDestroyed && !noteEditor.isEmpty);
export function focusNote() {
  if (noteEditor && !noteEditor.isDestroyed) focusEditor(noteEditor);
  else wantFocus = true;
}

/**
 * Primeiro item da grade (desktop) ou doca acima das abas (celular, `dock`). Com foco cresce e o resto desfoca.
 * O mesmo editor do detalhe: "/" (comandos), [[ (ligar a outra nota), imagem colada ou solta entra na nota (blob da
 * conta, como uma imagem da nota no detalhe) e Ctrl/Cmd+Shift+F abre o modo escrita com o rascunho.
 */
export function QuickNote({ dock }: { dock?: boolean }) {
  const [empty, setEmpty] = useState(!draft);
  const [focused, setFocused] = useState(false);
  const [writing, setWriting] = useState(false);
  const [edition, setEdition] = useState(0); // remonta o editor com o rascunho que voltou do modo escrita
  // Limpa só depois de gravar: se a escrita falhar (cota, IndexedDB bloqueado), o texto fica para tentar de novo.
  // Guardou: a nota volta ao tamanho de repouso e o foco vai para o card novo (ele aparece na grade, sem desfoque).
  // #etiqueta vira etiqueta do card; uma linha final só de etiquetas sai do texto (noteTags).
  // Imagem colada ainda subindo: espera ela entrar ("Enviando imagem…" na nota) e guarda o que está no editor.
  const submit = async (ed: Editor) => {
    if (saving) return;
    saving = true;
    try {
      await imagesSent(ed);
      if (ed.isDestroyed) return; // a nota saiu da grade: o rascunho fica (onSave ao desmontar)
      const typed = ed.getJSON() as Doc;
      const typedText = ed.getText();
      if (isBlank(typed, typedText)) return;
      const { doc, text, tags } = noteTags(typed, typedText);
      // Só um endereço (no celular, toque longo > Colar): vira card de link, com título e capa, não nota com o texto.
      const url = JSON.stringify(doc).includes('"type":"image"') ? null : urlFromText(text);
      const id = url ? await store.createLink(url, tags.length ? tags : undefined) : await store.createNote(doc, text);
      draft = null;
      if (url) toast({ key: `as-link:${id}`, text: "Guardado como link." });
      else {
        const fields = autoFields({ kind: "note", kindOrigin: "source", action: null, completedAt: null, content: null }, doc, text); // checklist = tarefa; aspas = citação; hex = cor
        if (Object.keys(fields).length) store.update(id, fields).catch(failed);
        for (const t of tags) store.attachTag(id, t).catch(failed);
      }
      if (ed.isDestroyed) return;
      ed.commands.clearContent(true);
      (ed.view.dom as HTMLElement).blur();
      // No toque o foco vai ao card novo sem o anel (o teclado virtual conta como teclado e o acenderia).
      refocus(() => document.querySelector<HTMLElement>(`a.card-open[href="#${CSS.escape(encodeURIComponent(id))}"]`), { preventScroll: true, focusVisible: !TOUCH });
    } catch {
      failed();
    } finally {
      saving = false;
    }
  };
  const openWriter = () => {
    if (noteEditor && !noteEditor.isDestroyed) {
      draft = noteEditor.isEmpty ? null : (noteEditor.getJSON() as Doc);
      (noteEditor.view.dom as HTMLElement).blur(); // o blur grava o rascunho (onSave) antes do modo escrita abrir
    }
    setWriting(true);
  };
  return (
    <section
      class={`card quicknote${dock ? " dock" : ""}`}
      aria-label="Nota rápida"
      onFocusIn={() => setFocused(true)}
      onFocusOut={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setFocused(false)}
      onKeyDown={(e) => {
        if (writing || !isWriterKey(e)) return;
        e.preventDefault();
        openWriter();
      }}
    >
      <p class="qn-label" aria-hidden="true">
        Nota rápida
      </p>
      {dock && (
        <button type="button" class="qn-close" onMouseDown={keepFocus} onClick={() => noteEditor && (noteEditor.view.dom as HTMLElement).blur()}>
          Fechar<span class="sr-only"> a nota (o rascunho fica)</span>
        </button>
      )}
      <RichText
        key={edition}
        doc={draft}
        id="quick-note"
        label="Nota rápida"
        placeholder={!focused ? "Escreva uma nota…" : TOUCH ? "Escreva… “/” abre os comandos" : `Escreva… “/” abre os comandos · ${MOD}+Enter guarda`}
        blurOnEsc
        links
        slash
        onSave={(doc, text) => (draft = isBlank(doc, text) ? null : doc)}
        onSubmit={(_doc, _text, ed) => void submit(ed)}
        onEmpty={setEmpty}
        onReady={(ed) => {
          noteEditor = ed;
          if (wantFocus) {
            wantFocus = false;
            focusEditor(ed);
          }
        }}
      />
      {!empty && (
        <button type="button" class="qn-save" onMouseDown={keepFocus} onClick={() => noteEditor && void submit(noteEditor)}>
          Guardar
          {!TOUCH && <kbd aria-hidden="true">{MOD} ↵</kbd>}
        </button>
      )}
      {writing && (
        <Writer
          card={{ id: "", content: draft } as Card} // rascunho: ainda não é card (nada é criado até guardar)
          onSave={(doc, text) => (draft = isBlank(doc, text) ? null : doc)}
          onClose={() => {
            setWriting(false);
            setEmpty(!draft);
            setEdition((n) => n + 1);
            wantFocus = true; // o editor novo pega o foco ao montar (onReady)
          }}
        />
      )}
    </section>
  );
}
